import React, { useMemo, useState } from "react";

import { difference } from "lodash";
import { useParams } from "react-router-dom";

import useAllAccountBalancesV4 from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalancesV4";
import { ClientId, TradingGroupId } from "@fartherfinance/frontend/api/Types";

import {
  ACCOUNTS_NO_TRADINGGROUP_TOOLTIP_TEXT,
  REALIZED_CAPITAL_GAINS_TOOLTIP_TEXT,
} from "../constants";
import CreateEditLayout from "../CreateEditLayout/CreateEditLayout";
import { useCreateEditTaxBudgetContext } from "../reducer/Context";
import { HypotheticalProposalResponseV2 } from "../reducer/types";
import { NO_TRADE_GROUP_ID } from "../SelectHypotheticalModels/constants";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";

import CapitalGainsTaxesTableV2, {
  HypotheticalProposalDisplay,
} from "./CapitalGainsTaxesTable/CapitalGainsTaxesTableV2";
import HeaderWithSubtext from "./HeaderWithSubtext/HeaderWithSubtext";
import HypotheticalHoldingsWeightsTableV2 from "./HypotheticalHoldingsWeightsTable/HypotheticalHoldingsWeightsTableV2";
import ScenariosSettings from "./ScenarioSettings/ScenarioSettings";

import styles from "./HypotheticalResultsAnalysis.module.css";

const HypotheticalResultsAnalysisV2: React.FC = () => {
  const { state } = useCreateEditTaxBudgetContext();
  const { clientId } = useParams<{ clientId: ClientId }>();

  const {
    data: {
      hypotheticalTradeGroups,
      hypotheticalProposalV2,
      clientAccountIds,
      excludedVirtualAccountIds,
    },
  } = state;

  const { proposalWithTaxBudget, proposalWithoutTaxBudget } = formatProposal(
    hypotheticalProposalV2
  );

  const auth = useAdvisorRequestAuth();

  const clientAuth = auth !== null ? { ...auth, clientId: clientId } : null;

  const accounts = difference(clientAccountIds, excludedVirtualAccountIds);

  const { data: balances } = useAllAccountBalancesV4(
    clientId,
    true,
    clientAuth
  );

  const fartherManagedAccountTotal = useMemo(
    () =>
      (balances ?? []).reduce(
        (total, balanceInfo) =>
          accounts.includes(balanceInfo.accountId)
            ? total + (balanceInfo.balance ?? 0)
            : total,
        0
      ),
    [balances, accounts]
  );

  const [selectedGroup, setSelectedGroup] = useState<TradingGroupId>(
    hypotheticalTradeGroups[0].tradingGroupId
  );

  return (
    <CreateEditLayout>
      <div className={styles.container}>
        <ScenariosSettings />

        <HeaderWithSubtext
          title={"Compare Capital Gains Taxes"}
          text={"Compare capital gains taxes with and without a tax budget"}
        >
          <TooltipBasic
            className={styles.infoIcon}
            text={REALIZED_CAPITAL_GAINS_TOOLTIP_TEXT}
          />
        </HeaderWithSubtext>

        <div className={styles.tableHeader}>
          Capital Gains Taxes: Breakdown of Realized Capital Gains
        </div>

        <CapitalGainsTaxesTableV2
          currentMarketValue={fartherManagedAccountTotal}
          budgetAmount={state.data.budgetAmount.toNumber()}
          proposalWithoutTaxBudget={proposalWithoutTaxBudget}
          proposalWithTaxBudget={proposalWithTaxBudget}
          anAccountIsTaxAdvantaged={state.data.hypotheticalTradeGroups.some(
            (group) => group.isTaxExempt
          )}
        />
        <HeaderWithSubtext
          title={"Compare Weights with a Tax Budget"}
          text={
            "Setting a tax budget may result in deviations in the weights of your holdings from their target model weights"
          }
        />

        <div className={styles.modelSelectionBar}>
          {hypotheticalTradeGroups.map((group) => {
            return (
              <div
                key={group.tradingGroupId}
                className={
                  selectedGroup === group.tradingGroupId
                    ? styles.tabSelected
                    : styles.tab
                }
                onClick={() => setSelectedGroup(group.tradingGroupId)}
              >
                <div>{group.accountsInPortfolio}</div>

                {group.tradingGroupId === NO_TRADE_GROUP_ID && (
                  <TooltipBasic
                    className={styles.infoIcon}
                    type={"secondary"}
                    text={ACCOUNTS_NO_TRADINGGROUP_TOOLTIP_TEXT}
                  />
                )}
              </div>
            );
          })}
        </div>

        <HypotheticalHoldingsWeightsTableV2
          isAnAccountUnder10_000={false}
          selectedTradingGroup={selectedGroup}
        />
      </div>
    </CreateEditLayout>
  );
};

export default HypotheticalResultsAnalysisV2;

type Result = {
  proposalWithTaxBudget: HypotheticalProposalDisplay;
  proposalWithoutTaxBudget: HypotheticalProposalDisplay;
};

const formatProposal = (
  proposal: HypotheticalProposalResponseV2 | undefined
): Result => {
  const proposalWithTaxBudget = {
    currentMarketValue: "N/A",
    ytdNetGainsRealized:
      proposal?.ytdNetGainWithTaxBudget
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    hypotheticalGainsRealized:
      proposal?.proposalsWithTaxBudget.totalGainsRealized
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    hypotheticalLossesHarvested:
      proposal?.proposalsWithTaxBudget.totalLossesHarvested
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    taxesPaid:
      proposal?.proposalsWithTaxBudget.totalTaxesPaid
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
  };

  const proposalWithoutTaxBudget = {
    currentMarketValue: "N/A",
    ytdNetGainsRealized:
      proposal?.ytdNetGainWithoutTaxBudget
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    hypotheticalGainsRealized:
      proposal?.proposalsWithoutTaxBudget.totalGainsRealized
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    hypotheticalLossesHarvested:
      proposal?.proposalsWithoutTaxBudget.totalLossesHarvested
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
    taxesPaid:
      proposal?.proposalsWithoutTaxBudget.totalTaxesPaid
        ?.toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
      "N/A",
  };

  return { proposalWithTaxBudget, proposalWithoutTaxBudget };
};
